import { RouteProp } from '@react-navigation/native';
import React from 'react';
import { ProfilesTabParamList, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import { getIntl } from '../../../../../utils/intl';
import { useReports } from '../../../../../hooks/api/useReports';
import { reportTypeMessages } from '../../../../../utils/sharedMessages';
import getDateString, {
    getAgeString,
    getIpLocationDescription,
    getParsedUserAgentDescription,
} from '../../../../../utils/getDateString';
import { usePromptButton } from '../../../../shared/PromptButton';
import { useSharedReportUrl } from './useSharedReportUrl';
import QRCode from 'react-native-qrcode-svg';
import { useDownloadQRCode } from './useDownloadQRCode';
import { useRerenderOnInterval } from '../../../../../hooks/useRerenderOnInterval';
import Alert from '../../../../v2BaseComponents/Alert';
import Table, { TableConfig } from '../../../../v2BaseComponents/Table';
import { Dynamo } from '../../../../../primary-record-types';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import Stack, { ContentStack } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import { PromptActions } from '../../../../v2BaseComponents/Prompt';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import TitledSection from '../../../../v2BaseComponents/TitledSection';
import LabelValueDisplay from '../../../../v2BaseComponents/LabelValueDisplay';
import IconButton from '../../../../v2BaseComponents/IconButton';
import SizedView from '../../../../v2BaseComponents/SizedView';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';
import useEvaluatedPermissions from '../../../../../hooks/api/useEvaluatedPermissions';
import { useCopyToClipboard } from '../../../../../hooks/useCopyToClipboard';
import useMedicalProfile from '../../../../../hooks/api/useMedicalProfile';
import getName from '../../../../../utils/getName';
import { ClickableLinkText } from '../../../../v2BaseComponents/Link';
import { dbIpAttributionUrl } from '../../../../../utils/constants';

const intl = getIntl('shared-report', {
    locationAttribution: 'Approximate viewer locations by ',
    dbIp: 'DB-IP',
    deleteSharedReport: 'Delete Shared Report',
    createdOn: 'Created on: {date}',
    areYouSure: 'Are you sure',
    confirmationText:
        'This will permanently delete the shared report.  This will invalidate the shared report link.  Anyone that has the link will no longer be able to view the report.',
    delete: 'Delete',
    cancel: 'Cancel',
    shareableURL: 'Shareable URL',
    shareableQRCode: 'Shareable QR Code',
    unableToViewLinkHeader: 'Unable to view link',
    unableToViewLinkMessage:
        'You are unable to view the link.  Only the person that created the report can view the link or the profile owner can view the link.',
    viewedOn: 'Date',
    device: 'Device',
    location: 'Location',
    viewHistory: 'View History',
    deletePromptTitle: 'Delete Shared Report?',
    sharing: 'Sharing',
    copyLink: 'Copy Link',
    shareViaLink: 'Share via Link',
    shareViaQRCode: 'Share via QR Code',
});

export type SharedReportScreenOwnProps = {};

export type SharedReportScreenProps = SharedReportScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'shared-report'>;
};

const viewsTableConfig: TableConfig<Dynamo.SharedReportView> = {
    columns: [
        {
            header: 'Viewed On',
            getContent: (data) => getDateString(data.viewDateTime, 'date-time') || '',
        },
        {
            header: 'Device',
            getContent: (data) => getParsedUserAgentDescription(data.parsedUserAgent) || '',
        },
        {
            header: 'Location',
            getContent: (data) => getIpLocationDescription(data.ipLocation) || '',
        },
    ],
};

const SharedReportScreen: React.FC<SharedReportScreenProps> = (props) => {
    const { copyToClipboard } = useCopyToClipboard({});
    const { navigate } = useProfilesTabNavigation();
    const { deleteSharedReport, isDeletingSharedReport, sharedReport, isLoadingSharedReport } = useReports({
        medicalProfileId: props.route.params.profileId,
        onDeleteSharedReport: () => {
            navigate('reports', {
                profileId: props.route.params.profileId,
            });
        },
        sharedReportId: props.route.params.sharedReportId,
        notifyOnProcessingComplete: true,
    });
    const { permissions } = useEvaluatedPermissions({ medicalProfileId: props.route.params.profileId });
    const { profile, isLoadingProfile } = useMedicalProfile({ medicalProfileId: props.route.params.profileId });
    useRerenderOnInterval({ interval: 100 }); // rerender because the ref update doesn't trigger a rerender
    const svgRef = React.useRef<any | null>(null);
    const { enableDownload, onDownloadQRCode } = useDownloadQRCode(
        svgRef.current?.elementRef?.current,
        profile && sharedReport
            ? {
                  name: getName(profile.contactDetails),
                  ageString: profile.contactDetails.dob ? getAgeString(profile.contactDetails.dob) : undefined,
                  reportName: reportTypeMessages(sharedReport.reportType),
              }
            : undefined,
    );
    const sharableUrl = useSharedReportUrl(sharedReport);
    const deletePromptButton = usePromptButton({
        promptTitle: intl('deletePromptTitle'),
        renderPromptContents: (onClose) => (
            <ContentStack>
                <Text variant={'body-1'}>{intl('confirmationText')}</Text>
                <PromptActions
                    actions={[
                        {
                            title: intl('delete'),
                            variant: 'alert',
                            icon: 'Trash2',
                            onPress: () => {
                                onClose();
                                deleteSharedReport(sharedReport!.sharedReportId);
                            },
                            testID: 'shared-report-delete-prompt-confirm',
                        },
                        {
                            title: intl('cancel'),
                            variant: 'secondary',
                            icon: 'XCircle',
                            onPress: onClose,
                        },
                    ]}
                />
            </ContentStack>
        ),
    });

    return (
        <>
            <AnimateLoadedContent
                isLoading={isLoadingSharedReport || !sharedReport || isDeletingSharedReport || isLoadingProfile}
            >
                {() => (
                    <>
                        <ProfileHeaderContainer profileId={props.route.params.profileId} bottomBorder>
                            <ActionButtonLayout
                                whiteBackground
                                scrollable
                                actions={[
                                    {
                                        show: permissions.permissions.reports === 'edit',
                                        variant: 'alert',
                                        icon: 'Trash2',
                                        title: intl('deleteSharedReport'),
                                        testID: 'shared-report-delete-button',
                                        onPress: deletePromptButton.onPress,
                                    },
                                ]}
                            >
                                <MainContentContainer>
                                    <Stack vertical fullWidth align="flex-start">
                                        <Text variant="h4">{reportTypeMessages(sharedReport!.reportType)}</Text>
                                        <Text variant="body-1">
                                            {intl('createdOn', {
                                                date: getDateString(sharedReport!.createdAt, 'date-time'),
                                            })}
                                        </Text>
                                    </Stack>
                                    <ContentStack>
                                        <TitledSection title={intl('viewHistory')}>
                                            <Table
                                                data={sharedReport!.views.sort(
                                                    (v1, v2) =>
                                                        new Date(v2.viewDateTime).getTime() -
                                                        new Date(v1.viewDateTime).getTime(),
                                                )}
                                                config={viewsTableConfig}
                                            />
                                        </TitledSection>
                                        {sharableUrl ? (
                                            <>
                                                <TitledSection
                                                    title={intl('shareViaLink')}
                                                    headerRight={
                                                        <IconButton
                                                            icon="Copy"
                                                            onPress={() => copyToClipboard(sharableUrl)}
                                                            variant="transparent"
                                                        />
                                                    }
                                                >
                                                    <ContentStack>
                                                        <LabelValueDisplay
                                                            items={[
                                                                {
                                                                    label: intl('shareableURL'),
                                                                    value: sharableUrl,
                                                                    linkType: 'url',
                                                                },
                                                            ]}
                                                        />
                                                    </ContentStack>
                                                </TitledSection>
                                                <TitledSection
                                                    title={intl('shareableQRCode')}
                                                    headerRight={
                                                        enableDownload ? (
                                                            <IconButton
                                                                icon="Download"
                                                                onPress={onDownloadQRCode}
                                                                variant="transparent"
                                                            />
                                                        ) : null
                                                    }
                                                >
                                                    <SizedView style={{ width: '100%' }}>
                                                        {({ width }) => (
                                                            <Stack
                                                                horizontal
                                                                justify="center"
                                                                fullWidth
                                                                testID={'shared-report-qr-code'}
                                                            >
                                                                <QRCode
                                                                    value={sharableUrl}
                                                                    size={Math.min(width, 400)}
                                                                    getRef={(ref) => (svgRef.current = ref)}
                                                                />
                                                            </Stack>
                                                        )}
                                                    </SizedView>
                                                </TitledSection>
                                            </>
                                        ) : (
                                            <Alert
                                                type="warning"
                                                title={intl('unableToViewLinkHeader')}
                                                message={intl('unableToViewLinkMessage')}
                                            />
                                        )}
                                        <Stack horizontal fullWidth justify="center">
                                            <Text variant="caption-1" alignText="center">
                                                {intl('locationAttribution')}
                                                <ClickableLinkText
                                                    linkType="url"
                                                    variant="caption-1"
                                                    linkLocation={dbIpAttributionUrl}
                                                >
                                                    {intl('dbIp')}
                                                </ClickableLinkText>
                                            </Text>
                                        </Stack>
                                    </ContentStack>
                                </MainContentContainer>
                            </ActionButtonLayout>
                        </ProfileHeaderContainer>
                    </>
                )}
            </AnimateLoadedContent>
            {deletePromptButton.Prompt}
        </>
    );
};

export default React.memo(SharedReportScreen);
